import router from '@/router'
import { nextTick } from 'vue'
import { getClientMeta, getMyUser, getOrganization } from '@/store'
import posthog from 'posthog-js'
import {
	createLogger,
	startCapturingConsole,
	stopCapturingConsole,
} from './log'
import { updateClientMeta } from '@/api/user'
import moment from 'moment'
import ProntoStorage from './storage'

const storage = new ProntoStorage('localStorage')

export const ENABLED_DURATION = moment.duration(1, 'week').asMilliseconds()
const CLIENT_META_KEY = 'web_session_recording'
const { VITE_POSTHOG_KEY } = import.meta.env
let disableRouterHook = null

const log = createLogger('SessionRecording')

export function startSession() {
	if (!hasUserEnabled()) {
		log('cannot start recording: disabled by user')
		return false
	}
	if (!posthog.__loaded) {
		initSession()
	}
	identifyUser()
	posthog.startSessionRecording()
	disableRouterHook = router.afterEach((to, from, failure) => {
		if (failure || !posthog.__loaded) return
		nextTick(() => posthog.capture('$pageview', { path: to.fullPath }))
	})
	log('started recording session')
}

export function endSession() {
	disableRouterHook?.()
	posthog.stopSessionRecording()
	posthog.reset()
	log('stopped recording session')
	stopCapturingConsole()
}

export function initSession() {
	if (!VITE_POSTHOG_KEY) {
		log('cannot initialize, missing VITE_POSTHOG_KEY key')
		return false
	}
	startCapturingConsole()
	log('initializing session')
	posthog.init(VITE_POSTHOG_KEY, {
		api_host: 'https://us.i.posthog.com',
		person_profiles: 'identified_only',
		disable_session_recording: true,
		enable_recording_console_log: true,
		capture_pageview: false,
		capture_pageleave: false,
		session_recording: {
			maskTextSelector: '[data-private]',
		},
	})
	if (!storage.has(CLIENT_META_KEY)) {
		storage.set(CLIENT_META_KEY, getUserEnabledPreference())
	}
}

export function identifyUser() {
	const user = getMyUser()
	const org = getOrganization()
	if (!user?.id || !org?.id) {
		log('cannot identify user', { user: user?.id, org: org?.id })
		return false
	}
	const distinctId = `${org.id}_${user.id}`
	posthog.identify(distinctId, { name: user.fullname })
	log('identified user', {
		distinctId,
		name: user.fullname,
	})
}

export function getUserEnabledPreference() {
	return getClientMeta(CLIENT_META_KEY) || storage.get(CLIENT_META_KEY)
}

export function hasUserEnabled() {
	const date = getUserEnabledPreference()
	if (!date) return false
	if (date === true) return true
	return moment().diff(date) < ENABLED_DURATION
}

export function getEnabledDate() {
	const date = getUserEnabledPreference()
	if (date === true) return true
	return date ? new Date(date) : null
}

export async function saveEnableRecording(enabled = false, noDate = false) {
	// This client_meta value can be set to one of these values:
	//   1. string - will be treated as a date and recording will stop after ENABLED_DURATION
	//   2. boolean - if true, recording will continue indefinitely
	let dateValue = null
	if (enabled && noDate) {
		dateValue = true
	} else if (enabled) {
		dateValue = new Date()
	}
	log('saving enabled preference', dateValue)
	if (dateValue) {
		storage.set(CLIENT_META_KEY, dateValue)
	} else {
		storage.remove(CLIENT_META_KEY)
	}
	await updateClientMeta({ [CLIENT_META_KEY]: dateValue })
	if (enabled) {
		startSession()
	} else {
		endSession()
	}
}
